import { useFlags } from '@atlaskit/flag';
import { flagConfigForRussianUsers, isCountryBlacklisted } from '@atlassian/amkt-common';
import { ButtonClickedEvent, useAnalyticsEvents } from '@atlassian/mpac-analytics';
import { getCurrentGeoIPRegion } from '@atlassian/mpac-api-client';
import { AppListingCTA } from '@atlassian/mpac-app-listing';
import { useStatsig } from '@atlassian/mpac-feature-flags';
import { useGetGeoIPDetails } from '@atlassian/mpac-primitives';
import {
  type HostingWithCloudComplianceBoundariesType,
  AppDetailsLink,
  ArtifactDownloadLink,
  HostingTypesWithCloudComplianceBoundaries,
  LicensePurchaseLink,
  ManagePrivateListingLink,
  TrialLicenseActivationLink,
} from '@atlassian/mpac-types';
import { AtlassianProductHostingType } from '@atlassian/mpac-types/graphql-gateway-types';
import { hooks, log } from '@atlassian/mpac-utils';
import React from 'react';

import {
  ANALYTICS_EVENT_SOURCE_APP_LISTING_PAGE,
  ANALYTICS_SUBJECT_ID_RUSSIAN_USER_BLOCKED,
} from '../../../../../../../analytics/Events';
import BootstrapConfig from '../../../../../../../bootstrapConfig';

import type {
  CTAKindType,
  HostingSelectorAppVersion,
  PurchaseIntentType,
  WatchersInfo,
} from '@atlassian/mpac-app-listing';
import type { MouseEvent } from 'react';

export type AllVersions = {
  cloudAppVersion: HostingSelectorAppVersion | null | undefined;
  serverAppVersion: HostingSelectorAppVersion | null | undefined;
  dataCenterAppVersion: HostingSelectorAppVersion | null | undefined;
  agcAppVersion: HostingSelectorAppVersion | null | undefined;
};

export type HeaderInjectionProps = {
  appKey: string;
  appId: string;
  appName: string;
  productHostingOptions: ReadonlyArray<HostingWithCloudComplianceBoundariesType>;
  selectedHosting: HostingWithCloudComplianceBoundariesType;
  versionInfo: AllVersions;
  artifactDownloadLink: ArtifactDownloadLink;
  trialLicenseActivationLink: TrialLicenseActivationLink;
  licensePurchaseLink: LicensePurchaseLink;
  privateListingsLink: ManagePrivateListingLink;
  privateRemotableOnly: boolean;
  appDetailsLink: AppDetailsLink;
  activeCTAKind: CTAKindType;
  hostingSelectorPurchaseIntent: PurchaseIntentType;
  onRequestAppClick: () => void;
  onAppListingHeaderCTAClick: (
    e: MouseEvent<HTMLElement>,
    productHostingOptions: ReadonlyArray<HostingWithCloudComplianceBoundariesType>,
    purchaseIntent: PurchaseIntentType,
    selectedVersion?: HostingSelectorAppVersion | null | undefined
  ) => void;
  onHostingSelectorCTAClick: (
    e: MouseEvent<HTMLElement>,
    hosting: AtlassianProductHostingType,
    appVersion: HostingSelectorAppVersion
  ) => void;
  watchersInfo: WatchersInfo | null | undefined;
  isLoggedIn?: boolean;
  loginHref: string;
};

export const getSelectedVersion = (
  selectedHosting: HostingWithCloudComplianceBoundariesType,
  versionInfo: AllVersions
): HostingSelectorAppVersion | null | undefined => {
  switch (selectedHosting) {
    case AtlassianProductHostingType.Cloud:
      return versionInfo.cloudAppVersion;
    case AtlassianProductHostingType.Server:
      return versionInfo.serverAppVersion;
    case AtlassianProductHostingType.DataCenter:
      return versionInfo.dataCenterAppVersion;
    case HostingTypesWithCloudComplianceBoundaries.IsolatedCloud:
      return versionInfo.cloudAppVersion;
    case HostingTypesWithCloudComplianceBoundaries.GovernmentCloud:
      return versionInfo.agcAppVersion;
    default:
      throw new Error('Unexpected hosting type: ' + selectedHosting);
  }
};

// eslint-disable-next-line max-lines-per-function
const HeaderInjection = (props: HeaderInjectionProps) => {
  const selectedVersion = getSelectedVersion(props.selectedHosting, props.versionInfo);
  const { showFlag } = useFlags();
  const { enableGeoIPFromStoreBff } = useStatsig();
  const { countryCode } = useGetGeoIPDetails();
  const fireAnalyticsEvent = useAnalyticsEvents();
  const { mpacBaseUrl } = BootstrapConfig.get();
  const { result: currentGeoIPRegionFromAMKT } = hooks.usePromise(() => {
    if (enableGeoIPFromStoreBff()) {
      return Promise.resolve(null);
    }
    return getCurrentGeoIPRegion(mpacBaseUrl);
  }, []);

  const blockAppInstallForRussia = async (
    e: MouseEvent<HTMLElement>,
    purchaseIntent: PurchaseIntentType
  ) => {
    try {
      if (isCountryBlacklisted(currentGeoIPRegionFromAMKT?.countryCode || countryCode)) {
        log.info('HeaderInjection', `Prevented app install for Russia`);
        e.preventDefault();
        fireAnalyticsEvent({
          eventType: ButtonClickedEvent,
          source: ANALYTICS_EVENT_SOURCE_APP_LISTING_PAGE,
          actionSubjectId: ANALYTICS_SUBJECT_ID_RUSSIAN_USER_BLOCKED,
        });
        // @ts-expect-error [MC-2850] - TS2345 - Argument of type 'FlagProps' is not assignable to parameter of type 'CreateFlagArgs'.
        showFlag(flagConfigForRussianUsers);
      } else {
        props.onAppListingHeaderCTAClick(
          e,
          props.productHostingOptions,
          purchaseIntent,
          selectedVersion
        );
      }
    } catch (error) {
      log.error('HeaderInjection', `Failed to retrieve geoIP`, { error });
    }
  };

  const handleCTAClick = (e: MouseEvent<HTMLElement>, purchaseIntent: PurchaseIntentType) => {
    blockAppInstallForRussia(e, purchaseIntent);
  };

  return (
    <>
      {selectedVersion && (
        <AppListingCTA
          appKey={props.appKey}
          appId={props.appId}
          appVersion={selectedVersion}
          privateRemotableOnly={props.privateRemotableOnly}
          selectedHosting={props.selectedHosting}
          artifactDownloadLink={props.artifactDownloadLink}
          trialLicenseActivationLink={props.trialLicenseActivationLink}
          licensePurchaseLink={props.licensePurchaseLink}
          onClick={handleCTAClick}
          privateListingsLink={props.privateListingsLink}
          watchersInfo={props.watchersInfo}
          isLoggedIn={props.isLoggedIn}
          loginHref={props.loginHref}
        />
      )}
    </>
  );
};

export default HeaderInjection;
